import { Injectable } from '@angular/core';
import { getDocument } from 'pdfjs-dist';
import { configurePdfWorker } from '../../../shared/pdf-worker';

import { extractValidCasNumbers } from '../validators/cas-number.extractor';
import { extractHStatementCodes } from '../validators/h-statement.extractor';

/**
 * Why a scan produced no auto-filled CAS number:
 *  - 'found'   — at least one valid CAS number was extracted
 *  - 'no-cas'  — text was read, but it contained no valid CAS number
 *  - 'no-text' — no readable text (scanned/image-only PDF, or unreadable file)
 */
export type SdsPdfScanOutcome = 'found' | 'no-cas' | 'no-text';

export interface SdsPdfScanResult {
  candidates: string[];
  outcome: SdsPdfScanOutcome;
}

/** Combined result of a single-pass document scan. */
export interface SdsPdfScanData {
  cas: SdsPdfScanResult;
  /**
   * Upper-cased, de-duplicated H/EUH codes found anywhere in the document —
   * the product's own classification plus the per-ingredient and section-16
   * listings (not yet resolved to catalog entries). Unlike the CAS scan this is
   * deliberately whole-document: the register wants the complete hazard set.
   */
  hStatementCodes: string[];
}

/** Below this many non-whitespace characters we treat the PDF as "no text". */
const MIN_MEANINGFUL_TEXT_LENGTH = 16;

/**
 * Extracts text from an SDS PDF in the browser (via pdfjs-dist) and mines it for
 * valid CAS Registry Numbers — scoped to the composition section (SDS section 3)
 * when that section can be located, see {@link extractValidCasNumbers}.
 * Everything runs client-side — the raw File never leaves the browser for this
 * step.
 *
 * Image-only / scanned PDFs yield no extractable text; those degrade gracefully
 * to an empty candidate list (no OCR is attempted).
 */
@Injectable({ providedIn: 'root' })
export class SdsPdfScannerService {
  /** Product-name default shared by single and bulk insertion. */
  getProductName(fileName: string | null | undefined): string {
    const trimmedName = (fileName || '').trim();
    if (!trimmedName) return '';
    const fileNameOnly = trimmedName.split(/[\\/]/).pop() || trimmedName;
    return fileNameOnly.replace(/\.[^./\\]+$/, '').trim() || fileNameOnly;
  }

  /** True when the file looks like a PDF (by MIME type or extension). */
  isPdf(file: { name?: string; type?: string } | null | undefined): boolean {
    if (!file) {
      return false;
    }
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    return type === 'application/pdf' || name.endsWith('.pdf');
  }

  /** Concatenated text content of every page. */
  async extractText(file: Blob): Promise<string> {
    configurePdfWorker();
    const buffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: new Uint8Array(buffer) }).promise;
    try {
      const pages: string[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);
        const content = await page.getTextContent();
        pages.push(
          content.items
            .map((item: any) => (typeof item?.str === 'string' ? item.str : ''))
            .join(' ')
        );
      }
      return pages.join('\n');
    } finally {
      await pdf.destroy();
    }
  }

  /**
   * De-duplicated, ranked valid CAS numbers found in the PDF, together with an
   * outcome that distinguishes "read the text but found no CAS" from "couldn't
   * read any text" (scanned/image-only or unreadable PDF). Never throws.
   */
  async findCasCandidates(file: Blob): Promise<SdsPdfScanResult> {
    let text: string;
    try {
      text = await this.extractText(file);
    } catch {
      return { candidates: [], outcome: 'no-text' };
    }

    if (text.replace(/\s/g, '').length < MIN_MEANINGFUL_TEXT_LENGTH) {
      return { candidates: [], outcome: 'no-text' };
    }

    const candidates = extractValidCasNumbers(text);
    return { candidates, outcome: candidates.length ? 'found' : 'no-cas' };
  }

  /**
   * Single-pass scan of a PDF: extracts the text once and mines it for both CAS
   * numbers and CLP/GHS hazard-statement codes (H / EUH). Never throws — an
   * unreadable/image-only PDF yields the 'no-text' CAS outcome and no H codes.
   */
  async scanDocument(file: Blob): Promise<SdsPdfScanData> {
    let text: string;
    try {
      text = await this.extractText(file);
    } catch {
      return { cas: { candidates: [], outcome: 'no-text' }, hStatementCodes: [] };
    }

    if (text.replace(/\s/g, '').length < MIN_MEANINGFUL_TEXT_LENGTH) {
      return { cas: { candidates: [], outcome: 'no-text' }, hStatementCodes: [] };
    }

    const cas = extractValidCasNumbers(text);
    return {
      cas: { candidates: cas, outcome: cas.length ? 'found' : 'no-cas' },
      hStatementCodes: extractHStatementCodes(text)
    };
  }
}
